import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Center, Container, LoadingOverlay } from '@mantine/core';
import { createOmniauthSession, getVendorPermissions } from '@/api';
import {
  resolveGqlErrors,
  setSessionToken,
  setUserEmail,
  setUserId,
  setVendors,
  trackGtmEvent,
} from '@/helpers';
import ErrorAlert from '@/components/ErrorAlert';
import { isValidRedirectUrl } from '@/helpers/url_validation';
import { Page, ROUTES } from '@/constants';
import { ALL_PAGE_DOMAINS } from '@/permissions';

const AuthCompletePage: React.FC = () => {
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [createSession, { loading }] = useMutation(createOmniauthSession);
  const [fetchVendorsWithPermissions, { loading: permissionsLoading }] =
    useLazyQuery(getVendorPermissions);

  useEffect(() => {
    const authenticate = async () => {
      const url = new URL(location.href);
      const nonce = url.searchParams.get('nonce');
      const redirectTo = url.searchParams.get('redirect_to');

      if (!nonce) {
        setError('Authorization failed. Please try again.');
        return;
      }

      try {
        const { data } = await createSession({ variables: { nonce } });
        const message = resolveGqlErrors(data?.createSession?.error);
        if (message) {
          setError(message);
          navigate(ROUTES.login);
          return;
        }

        if (!data?.createSession?.session) {
          setError('Authorization failed. Invalid token or user.');
          navigate(ROUTES.login);
          return;
        }

        const {
          token,
          user: { email, id, vendors },
        } = data.createSession.session;

        setSessionToken(token);
        setUserId(id);
        setUserEmail(email!);
        if (id) {
          trackGtmEvent('auth_complete_user_id', { user_id: id });
        }

        const { data: permissionsData } = await fetchVendorsWithPermissions({
          variables: {
            userId: id,
            entityType: 'Vendor',
            domains: ALL_PAGE_DOMAINS,
          },
        });

        const vendorPermissionIds = permissionsData?.getUser?.user?.permissions?.map(
          (p) => p.entityId
        );

        const vendorsWithPermissions = vendors?.filter((v) => vendorPermissionIds?.includes(v?.id));

        setVendors(vendorsWithPermissions || []);

        if (redirectTo) {
          const redirectPath = redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;
          if (isValidRedirectUrl(redirectPath)) {
            navigate(redirectPath);
          } else {
            navigate(ROUTES[Page.Dashboard]);
          }
        } else {
          navigate(ROUTES[Page.Dashboard]);
        }
      } catch (error) {
        const message = resolveGqlErrors(error);
        if (message) {
          setError(message);
        } else {
          console.error('An unexpected error occurred:', error);
        }
        navigate(ROUTES.login);
      }
    };
    authenticate();
  }, []);

  return (
    <Container fluid pos="relative" p="0" mih="100vh">
      <Center>
        <LoadingOverlay visible={loading || permissionsLoading} />
        {error && <ErrorAlert>{error}</ErrorAlert>}
      </Center>
    </Container>
  );
};

export default AuthCompletePage;
